/**
 * SOC Dashboard — Security Operations Center
 *
 * Style Grafana/Kibana :
 *   - Dark theme #070B11
 *   - JetBrains Mono
 *   - Threat feed live
 *   - Heatmap 24h security events
 *   - Audit chain integrity check
 *
 * v7.2 — branché sur /api/soc/* (live data)
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { safe } from "@/lib/safe-fetch";
import { trackCenter } from "@/lib/track-center";
import { useDensity } from "@/hooks/use-density";

export const Route = createFileRoute("/soc/")({
  component: SocDashboard,
});

interface SocMetrics {
  active_alerts: number;
  audit_events_24h: number;
  audit_by_action: Record<string, number>;
  iomt_live_devices: number;
}

interface FeedItem {
  kind: "audit" | "alert";
  ts: string;
  [k: string]: unknown;
}

function SocDashboard() {
  const { density } = useDensity();
  const [metrics, setMetrics] = useState<SocMetrics | null>(null);
  const [threats, setThreats] = useState<any>(null);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [auditChain, setAuditChain] = useState<{ total: number; broken: number; intact: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [obsLive, setObsLive] = useState<any>(null);

  const reload = async () => {
    try {
      const [m, t, f, ch, live] = await Promise.all([
        api.socMetrics(),
        api.socThreats(),
        api.socEventsFeed(),
        api.auditVerify(),
        safe(() => api.observabilityLive(), "soc.observabilityLive"),
      ]);
      setMetrics(m as any);
      setThreats(t);
      setFeed(((f as any).items || []) as FeedItem[]);
      setAuditChain(ch as any);
      setObsLive(live as any);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    trackCenter("soc");
    reload();
    if (!autoRefresh) return;
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  // 24h heatmap : bucketise les alertes par heure
  const heatmap = useMemo(() => {
    const buckets = new Array(24).fill(0).map((_, i) => ({ hour: i, critical: 0, warning: 0, info: 0 }));
    feed.forEach((e) => {
      if (e.kind !== "alert") return;
      const h = new Date(e.ts as string).getHours();
      const sev = (e.severity as string) || "info";
      if (sev === "critical") buckets[h].critical++;
      else if (sev === "warning") buckets[h].warning++;
      else buckets[h].info++;
    });
    return buckets;
  }, [feed]);

  const maxBucket = useMemo(() => {
    return Math.max(1, ...heatmap.map((b) => b.critical * 3 + b.warning * 2 + b.info));
  }, [heatmap]);

  return (
    <div className="soc-root p-6 space-y-4" data-density={density}>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 border-b border-cyan-500/20 pb-3">
        <div>
          <div className="flex items-center gap-3">
            <span className="soc-pulse" />
            <h1 className="text-2xl font-bold tracking-tight text-cyan-400">SOC // SECURITY OPERATIONS CENTER</h1>
          </div>
          <p className="text-xs text-slate-400 mt-1">Threat intelligence · Audit chain · Live feed · Compliance posture</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-400">REFRESH:</span>
          <button
            className={`px-2 py-1 rounded border ${autoRefresh ? "border-emerald-500/40 text-emerald-400" : "border-white/10 text-slate-400"}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? "● 5s" : "○ OFF"}
          </button>
          <span className="text-slate-500 ml-2">{new Date().toISOString().slice(0, 19).replace("T", " ")} UTC</span>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SocKpi label="ACTIVE_ALERTS" value={metrics?.active_alerts ?? 0} tone={metrics && metrics.active_alerts > 5 ? "crit" : "warn"} />
        <SocKpi label="AUDIT_24H" value={metrics?.audit_events_24h ?? 0} tone="default" />
        <SocKpi label="CHAIN_INTEGRITY" value={auditChain ? (auditChain.intact ? "INTACT" : "BROKEN") : "—"} tone={auditChain?.intact ? "ok" : "crit"} />
        <SocKpi label="IOMT_DEVICES" value={metrics?.iomt_live_devices ?? 0} tone="ok" />
        <SocKpi label="DATABASE" value="MEM-FB" tone="default" />
      </div>

      {/* v7.4 LIVE OBSERVABILITY STRIP */}
      {obsLive && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <SocKpi label="API p50" value={`${obsLive.api?.p50_ms ?? 0}ms`} tone="ok" />
          <SocKpi label="API p95" value={`${obsLive.api?.p95_ms ?? 0}ms`} tone={obsLive.api?.p95_ms > 200 ? "warn" : "ok"} />
          <SocKpi label="API p99" value={`${obsLive.api?.p99_ms ?? 0}ms`} tone={obsLive.api?.p99_ms > 500 ? "crit" : "warn"} />
          <SocKpi label="WS CLIENTS" value={obsLive.websocket?.connected_clients ?? 0} tone="default" />
          <SocKpi label="REDIS" value={`${obsLive.redis?.latency_ms ?? 0}ms`} tone={obsLive.redis?.status === "up" ? "ok" : "crit"} />
          <SocKpi label="ERR RATE 5M" value={`${obsLive.errors?.rate_pct ?? 0}%`} tone={obsLive.errors?.rate_pct > 1 ? "crit" : "ok"} />
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* THREAT FEED (1 col, but takes 2/3) */}
        <div className="soc-card lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-cyan-400">▸ THREAT_FEED [LIVE]</h2>
            <span className="text-xs text-slate-500">{feed.length} events</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto space-y-1 text-xs soc-mono">
            {feed.slice(0, 50).map((e, i) => (
              <div key={i} className="flex items-start gap-2 py-1 border-b border-white/5">
                <span className="text-slate-500 w-32 shrink-0">{(e.ts as string).slice(11, 19)}</span>
                <span className={`w-12 shrink-0 ${
                  e.kind === "alert" && e.severity === "critical" ? "text-red-400" :
                  e.kind === "alert" && e.severity === "warning" ? "text-amber-400" :
                  e.kind === "alert" ? "text-blue-400" : "text-emerald-400"
                }`}>
                  [{e.kind === "alert" ? (e.severity as string || "info").toUpperCase() : "AUDIT"}]
                </span>
                <span className="text-slate-300 flex-1">
                  {e.kind === "audit"
                    ? `${e.actor} → ${e.action} ${e.resource || ""}`
                    : `${e.title}${e.body ? " — " + e.body : ""}`}
                </span>
                {e.kind === "audit" && e.hash && (
                  <span className="text-slate-600 w-16 shrink-0">#{(e.hash as string).slice(0, 8)}</span>
                )}
              </div>
            ))}
            {feed.length === 0 && (
              <div className="text-slate-500 py-4 text-center">No events</div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-4">
          {/* Active threats */}
          <div className="soc-card">
            <h2 className="text-sm font-bold text-cyan-400 mb-3">▸ ACTIVE_THREATS</h2>
            <div className="space-y-2 text-xs">
              {threats?.recent_alerts?.slice(0, 6).map((a: any) => (
                <div key={a.id} className="flex items-start gap-2 py-1">
                  <span className={`w-2 h-2 mt-1 rounded-full ${
                    a.severity === "critical" ? "bg-red-500" :
                    a.severity === "warning" ? "bg-amber-500" : "bg-blue-500"
                  }`} />
                  <div className="flex-1">
                    <div className="text-slate-200">{a.title}</div>
                    <div className="text-slate-500">{a.source} · {new Date(a.created_at).toLocaleTimeString("fr-FR")}</div>
                  </div>
                </div>
              ))}
              {!threats?.recent_alerts?.length && <div className="text-slate-500">No active threats</div>}
            </div>
          </div>

          {/* Audit by action */}
          <div className="soc-card">
            <h2 className="text-sm font-bold text-cyan-400 mb-3">▸ AUDIT_ACTIONS_24H</h2>
            <div className="space-y-1 text-xs">
              {metrics && (metrics.audit_by_action ? Object.entries(metrics.audit_by_action) : []).map(([action, count]) => {
                const actionValues = metrics.audit_by_action ? Object.values(metrics.audit_by_action).map(Number) : [0];
                const pct = (count as number) / Math.max(...actionValues, 1) * 100;
                return (
                  <div key={action} className="flex items-center gap-2">
                    <span className="w-28 shrink-0 text-slate-400">{action}</span>
                    <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
                      <div className="h-full bg-cyan-500/60" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right tabular-nums">{count as number}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap 24h */}
      <div className="soc-card">
        <h2 className="text-sm font-bold text-cyan-400 mb-3">▸ ALERT_HEATMAP [24H UTC]</h2>
        <div className="flex gap-1">
          {heatmap.map((b, h) => {
            const intensity = (b.critical * 3 + b.warning * 2 + b.info) / maxBucket;
            const color = b.critical > 0 ? `rgba(239, 68, 68, ${0.3 + intensity * 0.7})` :
                          b.warning > 0 ? `rgba(245, 158, 11, ${0.3 + intensity * 0.7})` :
                          b.info > 0 ? `rgba(56, 189, 248, ${0.3 + intensity * 0.5})` :
                          "rgba(51, 65, 85, 0.2)";
            return (
              <div key={h} className="flex-1 flex flex-col items-center gap-1" title={`${h}h: ${b.critical}C ${b.warning}W ${b.info}I`}>
                <div className="w-full h-8 rounded" style={{ background: color }} />
                <span className="text-[10px] text-slate-500">{String(h).padStart(2, "0")}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/70" />Critical</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/70" />Warning</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-cyan-500/70" />Info</span>
          <span className="ml-auto">peak: {maxBucket}</span>
        </div>
      </div>

      <div className="text-xs text-slate-500 pt-3 border-t border-cyan-500/20 flex items-center justify-between">
        <span>DIKDOC_HUB v7.2 · SOC view</span>
        <Link to="/admin/siem" className="text-cyan-400 hover:underline">→ Retour SIEM</Link>
      </div>
    </div>
  );
}

function SocKpi({ label, value, tone = "default" }: { label: string; value: string | number; tone?: "default" | "warn" | "crit" | "ok" }) {
  const color = tone === "crit" ? "text-red-400" : tone === "warn" ? "text-amber-400" : tone === "ok" ? "text-emerald-400" : "text-cyan-400";
  return (
    <div className="soc-card">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-2 text-2xl font-bold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}